import { API, endpoints } from 'src/api';
import { useLoaderStore } from 'src/components/loaders';
import useSWR from 'swr';

export interface SettingsItemType {
  member: {
    firstname: string;
    lastname: string;
    role: string;
    avatar: string;
  };
  admin: boolean;
  memberCount: number;
  preferences: {
    timezone: {
      timezone?: any;
      dateformat?: any;
      firstday?: any;
    };
    notifications: {
      email: {
        creation_unlock: boolean;
        creation_invite: boolean;
        doc_invite: boolean;
        board_invite: boolean;
        organisation_invite: boolean;
      };
      activity: {
        someone_mentions_me: boolean;
        creation_doc_shared_with_me: boolean;
        collaboration_invite: boolean;
        microtask_assign: boolean;
        deadline_reminders: boolean;
      };
      other_email: {
        features_upgrades: true;
        tips_tricks: true;
        recommended_modules: true;
      };
    };
    audio_mode: string;
  };
}
export const useSettings = () => {
  const { data, error, revalidate, isValidating } = useSWR(endpoints.settings.url, API.get, {
    revalidateOnFocus: false,
  });

  const settingsData: any = { workspace: [], members: [], careerPath: [] };

  if (data?.data) {
    if (data.data.member) {
      settingsData.member = {
        firstname: data.data.member.firstname,
        lastname: data.data.member.lastname,
        role: data.data.member.role,
        email: data.data.member.email,
        avatar: data.data.member?.avatar,
        availability: data.data.member?.availability || { hours: 8 },
      };
    }

    settingsData.admin = data.data.admin;
    settingsData.owner = data.data.owner;
    settingsData.isOnlyAdmin = data.data.isOnlyAdmin;
    settingsData.isOwner = data.data.isOwner;
    settingsData.memberCount = data.data.memberCount;

    if (data?.data?.preferences) {
      const { preferences } = data.data;

      settingsData.preferences = {
        timezone: {
          timezone: preferences.timezone.timezone,
          dateformat: preferences.timezone.dateformat,
          firstday: preferences.timezone.firstday,
        },
        notifications: {
          email: {
            file_invite: preferences.notifications?.email.file_invite,
            organisation_invite: preferences.notifications?.email.organisation_invite,
            mention_file: preferences.notifications?.email.mention_file,
            workspace_invite: preferences.notifications?.workspace_invite,
          },
          alerts: {
            mentions: preferences.notifications?.alerts.mentions,
            creation_doc_shared: preferences.notifications?.alerts.creation_doc_shared,
            collaboration_invite: preferences.notifications?.alerts.collaboration_invite,
            microtask_assign: preferences.notifications?.alerts.microtask_assign,
            deadline_reminders: preferences.notifications?.alerts.deadline_reminders,
          },
          other_email: {
            new_features: preferences.notifications?.other_email.new_features,
            tips_n_tricks: preferences.notifications?.other_email.tips_n_tricks,
            recommended_modules: preferences.notifications?.other_email.recommended_modules,
          },
        },
        dashboard: {
          recent_templates: preferences.dashboard?.recent_templates,
          sketchnote_recommendations: preferences.dashboard?.sketchnote_recommendations,

          pro_tips: preferences.dashboard?.pro_tips,
          pro_tips_work: preferences.dashboard?.work_protips,

          recent_assigned_tasks: preferences.dashboard?.recent_assigned_tasks,
          keep_learning: preferences.dashboard?.keep_learning,

          continue_files: preferences.dashboard?.work_continue_files,
          favorite_files: preferences.dashboard?.work_favorite_files,

          loFi_work: preferences.dashboard?.work_lofi,
          loFi: preferences.dashboard?.lofi,

          blog_work: preferences.dashboard?.work_blog,
          blog: preferences.dashboard?.blog,

          ask_alex: preferences.dashboard?.ask_alex,
          ask_alex_learn: preferences.dashboard?.ask_alex_learn,

          calendar: preferences.dashboard?.calendar,
          your_workspaces: preferences.dashboard?.your_workspaces,

          upcoming_deadlines: preferences.dashboard?.upcoming_deadlines,
          incomplete_tasks: preferences.dashboard?.incomplete_tasks,

          sketchnote_ai: preferences.dashboard?.sketchnote_ai,

          get_started: preferences.dashboard?.get_started,
          quick_notepad: preferences.dashboard?.quick_notepad,
          embed_anything: preferences.dashboard?.embed_anything,
          roadmap_work: preferences.dashboard?.roadmap_work,
          helpcenter_work: preferences.dashboard?.helpcenter_work,
          changelog_work: preferences.dashboard?.changelog_work,
        },
        audio_mode: preferences?.audio_mode,
        workLearnMode: preferences?.op_mode,
      };
    }
    settingsData.organisation = {
      name: data.data.organisation?.name,
      organisation_avatar: data.data.organisation?.avatar,
      organisation_logo: data.data.organisation?.logo,
    };
    if (data.data.workspace) {
      settingsData.workspace = {
        workspace: {
          workspaceId: data.data.workspaces.workspaceId,
          name: data.data.workspaces.name,
          isCustom: data.data.workspaces.isCustom,
          isPublic: data.data.workspaces.isPublic,
          members: data.data.workspaces.members,
        },
      };
    }

    if (data?.data?.career_path) {
      settingsData.careerPath = {
        stage: data?.data?.career_path?.stage,
        role: data?.data?.career_path.role,
        fow: data?.data?.career_path?.fow,
        interest: data?.data?.career_path?.categories,
      };
    }
    if (data?.data?.members) {
      settingsData.members = data.data.members;
    }
    if (data?.data?.guests) {
      settingsData.guests = data?.data?.guests;
    }
    if (data?.data?.invitees) {
      const invites = data?.data?.invitees;
      settingsData.pendingInviteMembers = invites.filter((member) => {
        return member.role === 'member' || member.role === 'admin';
      });
      settingsData.pendingInviteGuests = invites.filter((member) => member.role === 'guest');
    }
  }

  return {
    settingsData,
    error,
    revalidate,
    isValidating,
    isLoading: !data && !error,
  };
};
